/**
 * 模型作品
 */

import { request } from "../request";
import type {
  ListResponse,
  ModelItem,
  ModelDetail,
  ModelLabels,
  VideoModelItem,
} from "@xmiles/holopix-types";

/**
 * 获取公开的模型作品列表
 */
export const getPublicModelList = (
  data: {
    pageNo?: number;
    pageSize?: number;
    showType?: "hot" | "new" | "collect";
    targetUserId?: number;
  } = {
    pageNo: 1,
    pageSize: 10,
    showType: "new",
  }
) => {
  return request<ListResponse<ModelItem[]>>({
    url: "/pino_service_api/external/externalModelPublishList",
    method: "post",
    data: {
      data: {
        notUserModel: true,
        isPublishModel: true,
        ...data,
      },
    },
    isEncryption: true,
  });
};

/**
 * 获取模型作品详情
 */
export const getPublicModelDetail = (data: { id: number | string, userId?: number }) => {
  return request<ModelDetail>({
    url: "/pino_service_api/external/modelPublishDetail",
    method: "post",
    data: {
      data,
    },
    isEncryption: true,
  });
};

/**
 * 获取模型标签
 */
export const getModelLabels = () => {
  return request<ModelLabels>({
    url: "/pino_service_api/external/labelInfo",
    method: "get",
    isAuth: true,
    isEncryption: true,
  });
};

/**
 * 获取使用模型的详情
 * AI创作页面加载模型使用
 */
export const getModelDetail = (data: { id: number | string }) => {
  return request<ModelDetail>({
    url: "/pino_service_api/api/model/detail",
    method: "post",
    data: {
      data,
    },
    isAuth: true,
    isEncryption: true,
  });
};

/**
 * 获取模型详情
 */
export const getExternalModelDetail = (data: { id: number | string }) => {
  return request<ModelDetail>({
    url: "/pino_service_api/external/externalModelDetail",
    method: "post",
    data: {
      data,
    },
    isAuth: true,
    isEncryption: true,
  });
};

/**
 * 获取我的模型列表
 */
export const getMyModelList = (data: {
  pageNo: number;
  pageSize: number;
  /**
   * 是否只要本人未发布的模型
   */
  onlyUserModelAndOnlyNotPublish: boolean;
  loraType?: "Lora" | "Style" | "Controller" | null;
}) => {
  return request<ListResponse<ModelItem[]>>({
    url: "/pino_service_api/api/model/modelPublishList",
    method: "post",
    data: {
      data: {
        onlyUserModel: true,
        ...data,
      }
    },
    isAuth: true,
    isEncryption: true,
  });
};

/**
 * 根据用户ID筛选模型广场的模型列表
 */
export const getPublishedModelListByUserId = (data: {
  pageNo: number;
  pageSize: number;
  targetUserId: string | number;
}) => {
  return request<ListResponse<ModelItem[]>>({
    url: "/pino_service_api/api/model/modelPublishList",
    method: "post",
    data: {
      data: {
        showType: "user",
        isPublishModel: true,
        ...data,
      }
    },
    isAuth: true,
    isEncryption: true,
  });
};

/**
 * 点赞模型作品
 */
export const likeModel = (data: {
  /** 模型作品ID */
  modelPostsId: number;
}) => {
  return request({
    url: "/pino_service_api/api/like/addLike",
    method: "post",
    data: {
      data: {
        likeRelatedId: data.modelPostsId,
        likeType: 2,
      },
    },
    isAuth: true,
    isEncryption: true,
  });
};

/**
 * 取消点赞模型作品
 */
export const cancelLikeModel = (data: {
  /** 模型作品ID */
  modelPostsId: number;
}) => {
  return request({
    url: "/pino_service_api/api/like/cancelLike",
    method: "post",
    data: {
      data: {
        likeRelatedId: data.modelPostsId,
        likeType: 2,
      },
    },
    isAuth: true,
    isEncryption: true,
  });
};

/**
 * 收藏模型作品
 */
export const collectModel = (data: {
  /** 模型作品ID */
  modelPostsId: number;
}) => {
  return request({
    url: "/pino_service_api/api/model/collectModelPublish",
    method: "post",
    data: {
      data: {
        modelId: data.modelPostsId,
        collect: 1,
      },
    },
    isAuth: true,
    isEncryption: true,
  });
};

/**
 * 取消收藏模型作品
 */
export const cancelCollectModel = (data: {
  /** 模型作品ID */
  modelPostsId: number;
}) => {
  return request({
    url: "/pino_service_api/api/model/collectModelPublish",
    method: "post",
    data: {
      data: {
        modelId: data.modelPostsId,
        collect: 0,
      },
    },
    isAuth: true,
    isEncryption: true,
  });
};

/**
 * 发布模型
 */
export const publishModel = (data: {
  name: string;
  loraType: "LoRA" | "Style" | "KleinFlux";
  label: number[];
  modelVersions: {
    id?: string;
    modelVerName: string;
    modelId: number;
    intro: string;
    previews: string;
    comparisonImg?: string;
    geneFlag: boolean;
    mixFlag: boolean;
    businessFlag: boolean;
  }[];
}) => {
  return request({
    url: "/pino_service_api/api/modelPublish/submit",
    method: "post",
    data: {
      data,
    },
    isAuth: true,
    isEncryption: true,
  });
};

/**
 * 获取模型发布详情
 * @param id 模型ID
 */
export const getModelPublishDetail = (id: number) => {
  return request<ModelDetail>({
    url: "/pino_service_api/api/modelPublish/detail",
    method: "post",
    data: {
      data: {
        id
      }
    },
    isAuth: true,
    isEncryption: true,
  });
};

/**
 * 获取视频模型列表
 */
export const getVideoModelList = (data: {
  /** 分页参数，页码 */
  pageNo: number;
  /** 分页参数，每页数量 */
  pageSize: number;
}) => {
  return request<ListResponse<VideoModelItem[]>>({
    url: "/pino_service_api/api/model/videoModelList",
    method: "post",
    data: {
      data
    },
    isAuth: true,
    isEncryption: true,
  });
};
